import { useState, useCallback } from 'react';
import { StarRating } from './StarRating';
import { ParkerScaleInput } from './ParkerScaleInput';
import { ReviewTextFields } from './ReviewTextFields';
import { ReviewPhotoUpload, type ReviewPhoto } from './ReviewPhotoUpload';
import { Button } from '../ui/Button';
import { FormSection } from '../ui/FormSection';
import type { ReviewPhotoUploadStates } from '../../hooks/useReviewPhotoUpload';

export interface WineReviewFormData {
  ratings: {
    score: number;
    valueForMoney: number;
  };
  title: string;
  body: string;
  photos: ReviewPhoto[];
}

export interface WineReviewFormProps {
  initialData?: Partial<WineReviewFormData>;
  onSubmit: (data: WineReviewFormData) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** Per-photo upload progress reported by the parent while the review is submitting. */
  photoUploadStates?: ReviewPhotoUploadStates;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_DATA: WineReviewFormData = {
  ratings: {
    score: 75,
    valueForMoney: 0,
  },
  title: '',
  body: '',
  photos: [],
};

export const WineReviewForm = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  photoUploadStates,
  disabled = false,
  className = '',
}: WineReviewFormProps) => {
  const [formData, setFormData] = useState<WineReviewFormData>({
    ...DEFAULT_DATA,
    ...initialData,
    ratings: { ...DEFAULT_DATA.ratings, ...initialData?.ratings },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleScoreChange = useCallback((value: number) => {
    setFormData((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, score: value },
    }));
    setErrors((prev) => ({ ...prev, score: '' }));
  }, []);

  const handleValueChange = useCallback((value: number) => {
    setFormData((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, valueForMoney: value },
    }));
    setErrors((prev) => ({ ...prev, valueForMoney: '' }));
  }, []);

  const handleTitleChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, title: value }));
    setErrors((prev) => ({ ...prev, title: '' }));
  }, []);

  const handleBodyChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, body: value }));
    setErrors((prev) => ({ ...prev, body: '' }));
  }, []);

  const handlePhotosChange = useCallback((photos: ReviewPhoto[]) => {
    setFormData((prev) => ({ ...prev, photos }));
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate score
    if (formData.ratings.score < 50 || formData.ratings.score > 100) {
      newErrors.score = 'Score must be between 50 and 100';
    }

    // Validate value for money
    if (formData.ratings.valueForMoney === 0) {
      newErrors.valueForMoney = 'Please rate Value for Money';
    }

    // Validate title
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 120) {
      newErrors.title = 'Title must be 120 characters or less';
    }

    // Validate body (wine reviews have 10,000 char max)
    if (!formData.body.trim()) {
      newErrors.body = 'Review body is required';
    } else if (formData.body.length < 100) {
      newErrors.body = 'Review must be at least 100 characters';
    } else if (formData.body.length > 10000) {
      newErrors.body = 'Review must be 10,000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const [submitError, setSubmitError] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitError('');
    try {
      await onSubmit(formData);
    } catch (error) {
      // Photo uploads happen during submission, so a failure here is the only
      // place the user learns that an image never made it to the server.
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Something went wrong while submitting your review. Please try again.'
      );
    }
  }, [validate, onSubmit, formData]);

  const isFormDisabled = disabled || isSubmitting;

  return (
    <div className={className}>
      {/* Ratings Section */}
      <FormSection title="Rate This Wine" titleAs="h3">
        <div className="space-y-6">
          <ParkerScaleInput
            label="Wine Score (Parker Scale)"
            value={formData.ratings.score}
            onChange={handleScoreChange}
            disabled={isFormDisabled}
            error={errors.score}
            required
          />

          <StarRating
            label="Value for Money"
            value={formData.ratings.valueForMoney}
            onChange={handleValueChange}
            disabled={isFormDisabled}
            error={errors.valueForMoney}
            required
          />
        </div>
      </FormSection>

      {/* Text Fields Section */}
      <ReviewTextFields
        title={formData.title}
        body={formData.body}
        onTitleChange={handleTitleChange}
        onBodyChange={handleBodyChange}
        bodyMaxLength={10000}
        disabled={isFormDisabled}
        titleError={errors.title}
        bodyError={errors.body}
      />

      {/* Photo Upload Section */}
      <ReviewPhotoUpload
        photos={formData.photos}
        onPhotosChange={handlePhotosChange}
        disabled={isFormDisabled}
        uploadStates={photoUploadStates}
      />

      {submitError && (
        <div
          className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
          role="alert"
        >
          <p>{submitError}</p>
          <p className="mt-1 text-red-600">
            Photos that already uploaded will not be sent again.
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isFormDisabled}
            className="mt-2 font-medium underline hover:no-underline disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
          >
            Try again
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-3 border-t border-gray-200 pt-6">
        <Button variant="primary" onClick={handleSubmit} disabled={isFormDisabled} loading={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};
